import { parseSettings } from "@cuekards/core";
import type { Settings } from "@cuekards/core";
import {
  SETTINGS_STORAGE_KEY,
  readStoredSettings,
  type SettingsStorage,
} from "../src/settings-bridge.js";

/**
 * The page's own copy of the settings blob, kept current as the worker writes.
 *
 * The page is already where the blob lives — it reads it at startup and stores
 * every write the core worker posts back — so the export and the shareable link
 * need no channel on the contract at all. What they *do* need is to be looking
 * at the blob as it is now. Reading storage once while a screen renders is the
 * bug that hides here: the settings screen's rows each hold their own state, so
 * changing a font size re-renders a row and nothing above it, and a file
 * exported afterwards would be the setup from before the change.
 *
 * Not React state lifted somewhere, for the reason `./take-notice.ts` is not:
 * what writes to it is the settings bridge, attached to the port once at
 * startup and outside the component tree, so it has to outlive a render.
 */

let settings: Settings = {};
const listeners = new Set<() => void>();

/**
 * Persist through `storage`, and keep the mirror current.
 *
 * A wrapper rather than another argument to `attachSettingsPersistence`,
 * because "what is in storage" is exactly what a mirror of storage should
 * follow — and because the bridge is shared with the core worker, which has no
 * screen to keep current.
 */
export function mirroredStorage(storage: SettingsStorage): SettingsStorage {
  remember(readStoredSettings(storage));
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => {
      storage.setItem(key, value);
      if (key === SETTINGS_STORAGE_KEY) remember(parseSettings(value));
    },
  };
}

/** What this browser has stored, as of the last write. */
export function storedSettings(): Settings {
  return settings;
}

/** Subscribe to it. The snapshot is the held object rather than a fresh copy,
 * so an unchanged blob is an unchanged reference and nothing re-renders. */
export function subscribeToSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function remember(next: Settings): void {
  settings = next;
  for (const listener of listeners) listener();
}
